import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/lib/i18n";
import { trackStoreClick } from "@/lib/analytics";
import { useStoreLinks } from "@/lib/stores";
import { GooglePlayLogo } from "@/components/common/StoreBadges";
import { blogContent } from "./content";

export function VerdictSection() {
  const { language } = useLanguage();
  const c = blogContent[language].verdict;

  const { appStoreUrl, playStoreUrl } = useStoreLinks(language);

  return (
    <section className="mx-auto max-w-[680px] px-4 sm:px-6 pb-16">
      <h2 className="font-['Source_Serif_4'] text-[1.75rem] sm:text-[2rem] leading-tight font-semibold text-foreground mt-4 mb-5">
        {c.title}
      </h2>

      <p className="font-['Source_Serif_4'] text-[1.0625rem] sm:text-lg leading-[1.75] text-foreground/85 mb-6">
        {c.intro}
      </p>

      <ul className="space-y-5 mb-12">
        {c.choices.map((choice, i) => {
          const highlight = choice.label === "ReFound";
          return (
            <li
              key={i}
              className="font-['Source_Serif_4'] text-[1.0625rem] sm:text-lg leading-[1.75] text-foreground/85 pl-6 relative"
            >
              <span className={`absolute left-0 top-[0.7em] h-1.5 w-1.5 rounded-full ${highlight ? "bg-primary" : "bg-muted-foreground/40"}`} />
              {c.chooseWord}{" "}
              <strong className={highlight ? "text-primary font-semibold" : "text-foreground font-semibold"}>
                {choice.label}
              </strong>
              {choice.example ? <span className="text-muted-foreground"> {choice.example}</span> : null}
              {" "}{choice.description}
            </li>
          );
        })}
      </ul>

      <div className="border-t border-border/30 pt-10">
        <p className="font-['Source_Serif_4'] text-[1.0625rem] sm:text-lg leading-[1.75] text-foreground/85 mb-3">
          {c.ctaP1Start}
          <strong className="text-foreground font-semibold">{c.ctaP1Strong}</strong>
          {c.ctaP1End}
        </p>
        <p className="font-['Source_Serif_4'] text-[1.0625rem] sm:text-lg leading-[1.75] text-foreground/85 mb-8">
          {c.ctaP2}
        </p>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            size="lg"
            className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-lg"
            asChild
          >
            <a
              href={appStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackStoreClick("app_store", "blog_verdict", language)}
              className="inline-flex items-center gap-2"
            >
              {c.ctaButton}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="group font-semibold rounded-lg"
            asChild
          >
            <a
              href={playStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackStoreClick("google_play", "blog_verdict", language)}
              className="inline-flex items-center gap-2"
            >
              <GooglePlayLogo className="h-4 w-4" />
              {c.ctaButtonPlay}
            </a>
          </Button>
        </div>
      </div>

      <div className="mt-16 pt-8 border-t border-border/30 flex items-center gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/15 text-primary font-bold text-base font-['Funnel_Display']">
          R
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">{c.authorName}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{c.authorBio}</p>
        </div>
      </div>
    </section>
  );
}
